import {DOM} from "./DOM";
import {Loader} from "./Loader";
import {Storage} from "./Storage";
import {Modal} from "./Modal";
import {INetworkingCreateRequestParameters} from "../definitions/utils/networking/INetworkingCreateRequestParameters";
import {RequestTypes} from "../definitions/utils/networking/RequestTypes";
import {PageTitle} from "../definitions/utils/networking/PageTitle";
import {NetworkControllerStorageData} from "../definitions/utils/networking/NetworkControllerStorageData";
import {Notify} from "./Notify";
import {SentryReportNetworkingParseException, SentryReportNetworkingXHR, SentryReportNetworkingXHRTimeout} from "./Sentry/Sentry";
import {ControlsGeneralTableControl} from "../../../app/controls/General/TableControl/TableControl";

export namespace Networking {

    export class NetworkingController {

        /**
         * Create new networking request
         * @param requestParameters
         */
        public static async createRequest(requestParameters: INetworkingCreateRequestParameters): Promise<void> {

            // Initialize connection and loader
            const xhr = new XMLHttpRequest();
            xhr.open(requestParameters.type.toString(), requestParameters.url, true);
            xhr.setRequestHeader("X-Requested-With", "XMLHttpRequest");

            const requestsIdentifier = `${requestParameters.type}/${requestParameters.url}`;

            // Check is uniq
            if (!requestParameters.disableUniq) {
                if (this.alreadyRunningRequestByIdentifier(requestsIdentifier)) {
                    console.warn(`${requestParameters.type}/${requestParameters.url} is not unique`);
                    return;
                }
            }
            if (!requestParameters.silent) { Loader.LoaderController.appendRequest(); }
            this.appendRequest(requestsIdentifier);

            if (requestParameters.startCallback) { requestParameters.startCallback(); }
            if (requestParameters.disableButtons && requestParameters.disableButtons.length > 0) {
                for (const disableButton of requestParameters.disableButtons) {
                    disableButton.disabled = true;
                }
            }

            console.info(`${requestParameters.type}/${requestParameters.url} requested...`);
            if (requestParameters.type === RequestTypes.POST || requestParameters.type === RequestTypes.PUT) {
                if (requestParameters.formData) {
                    xhr.send(requestParameters.formData);
                } else {
                    const requestData = requestParameters.data ? JSON.stringify(requestParameters.data) : JSON.stringify({});
                    xhr.setRequestHeader("Content-Type", "application/json;charset=UTF-8");
                    xhr.send(requestData);
                }

            } else { xhr.send(); }

            // Stop on redirect
            this.processAbortOnRedirect(xhr);

            // Timeout
            xhr.ontimeout = () => {
                this.removeRequest(requestsIdentifier);
                if (!requestParameters.silent) { Loader.LoaderController.removeRequest(); }
                SentryReportNetworkingXHRTimeout(xhr, requestParameters);
                if (requestParameters.errorCallback) { requestParameters.errorCallback(); }
                if (requestParameters.completedCallback) { requestParameters.completedCallback(); }
                if (requestParameters.disableButtons && requestParameters.disableButtons.length > 0) {
                    for (const disableButton of requestParameters.disableButtons) {
                        disableButton.disabled = false;
                    }
                }
                return;
            }

            // Abort
            xhr.onabort = () => {
                this.removeRequest(requestsIdentifier);
                if (!requestParameters.silent) { Loader.LoaderController.removeRequest(); }
                console.warn(`${requestParameters.type}/${requestParameters.url} returns abort`);
                return;
            };

            // Error
            xhr.onerror = () => {

                this.removeRequest(requestsIdentifier);
                if (!requestParameters.silent) { Loader.LoaderController.removeRequest(); }
                if (requestParameters.completedCallback) { requestParameters.completedCallback(); }
                if (requestParameters.disableButtons && requestParameters.disableButtons.length > 0) {
                    for (const disableButton of requestParameters.disableButtons) {
                        disableButton.disabled = false;
                    }
                }

                // IS-3904: Probable when moved to another page and request not finished
                if (xhr.status === 0 && xhr.readyState === 4) {
                    console.warn(`${requestParameters.type}/${requestParameters.url} forced to quit (onerror)`);
                    return;
                }

                SentryReportNetworkingXHR(xhr, requestParameters);
                if (requestParameters.errorCallback) { requestParameters.errorCallback(); }
                return;
            };

            // Finish
            xhr.onloadend = () => {
                this.removeRequest(requestsIdentifier);
                if (!requestParameters.silent) { Loader.LoaderController.removeRequest(); }
            }

            // Request completed
            xhr.onload = () => {

                // Wrong HTTP code returned
                if (xhr.status !== 200) {

                    if (requestParameters.completedCallback) { requestParameters.completedCallback(); }
                    if (requestParameters.disableButtons && requestParameters.disableButtons.length > 0) {
                        for (const disableButton of requestParameters.disableButtons) {
                            disableButton.disabled = false;
                        }
                    }

                    // IS-3904: Probable when moved to another page and request not finished
                    if (xhr.status === 0 && xhr.readyState === 4) {
                        console.warn(`${requestParameters.type}/${requestParameters.url} forced to quit (onload)`);
                        return;
                    }

                    SentryReportNetworkingXHR(xhr, requestParameters);
                    if (requestParameters.errorCallback) { requestParameters.errorCallback(); }
                    return;
                }

                // Don't process payload
                if (requestParameters.dontParsePayload) {
                    if (requestParameters.successCallback) { requestParameters.successCallback(); }
                    if (requestParameters.completedCallback) { requestParameters.completedCallback(); }
                    if (requestParameters.disableButtons && requestParameters.disableButtons.length > 0) {
                        for (const disableButton of requestParameters.disableButtons) {
                            disableButton.disabled = false;
                        }
                    }
                    return;
                }

                // IS-3854: Fix some payload issues
                const xhrResponse : string = xhr.responseText.replace('OK', '');

                // Don't parse returned data
                if (requestParameters.rawJsonResponse) {
                    if (requestParameters.successCallback) { requestParameters.successCallback(xhrResponse); }
                    if (requestParameters.completedCallback) { requestParameters.completedCallback(xhrResponse); }
                    if (requestParameters.disableButtons && requestParameters.disableButtons.length > 0) {
                        for (const disableButton of requestParameters.disableButtons) {
                            disableButton.disabled = false;
                        }
                    }
                    return;
                }

                // Parse and process returned data
                let parsedPayload = null;
                let processedResult = null;
                try {

                    // Process payload
                    parsedPayload = JSON.parse(xhrResponse);
                    let subPayload = (parsedPayload.response) ? JSON.parse(parsedPayload.response) : null;
                    parsedPayload = subPayload ? {...parsedPayload, ...subPayload} : parsedPayload;

                    // Do payload action list
                    if (!requestParameters.dontProcessPayload) {
                        processedResult = NetworkingController.processPayloadFromServer(parsedPayload, requestParameters, xhr);
                    }

                    // Resolve payload (if not processed OR if not failed and not redirect)
                    console.info(`${requestParameters.type}/${requestParameters.url} success.`);
                    if (requestParameters.successCallback && (processedResult === null || (!processedResult.failed && !processedResult.redirect))) {
                        requestParameters.successCallback(parsedPayload);
                    }

                } catch (e) {
                    SentryReportNetworkingParseException(xhr, e, requestParameters);
                } finally {
                    if (requestParameters.completedCallback && (processedResult === null || !processedResult.redirect)) {
                        requestParameters.completedCallback(parsedPayload);
                    }
                    if (requestParameters.disableButtons && requestParameters.disableButtons.length > 0) {
                        for (const disableButton of requestParameters.disableButtons) {
                            disableButton.disabled = false;
                        }
                    }
                }
            };
        }

        /**
         * Process payload data from server on FE
         * Payload fails if contains error notify + empty snippets
         * @param parsedPayload - decoded parsed payload data
         * @param requestParams
         * @param xhr
         * @return boolean - contains failed payload
         * @private
         */
        private static processPayloadFromServer(parsedPayload: any, requestParams: INetworkingCreateRequestParameters, xhr: XMLHttpRequest) :
            { failed?: boolean, redirect?: boolean } {

            if (parsedPayload.pageTitle) { NetworkingController.processPayloadTitle(parsedPayload.pageTitle); }
            if (parsedPayload.snippets) { NetworkingController.processPayloadSnippets(parsedPayload.snippets, requestParams, xhr); }
            if (parsedPayload.wrongInputs) { NetworkingController.processWrongInputs(parsedPayload.wrongInputs); }

            if (parsedPayload.flashMessages) {
                const modalCloseByNotify: boolean = Notify.Handler.isModalCloseByNotify(parsedPayload.flashMessages);
                Notify.Handler.processMessages(parsedPayload.flashMessages, requestParams.muteSuccess ?? false, modalCloseByNotify);
                if (Notify.Handler.isErrorByNotify(parsedPayload.flashMessages) && !parsedPayload.snippets) { return {failed: true}; }
                if (modalCloseByNotify) {
                    NetworkingController.processCloseModal();
                }
            }

            if (parsedPayload.redirect) {
                NetworkingController.processPayloadRedirect(parsedPayload.redirect, !!parsedPayload.flashMessages);
                return {redirect: true};
            }

            return {};
        }

        /**
         * Update wrong input elements
         * @param wrongInputs - list of input names
         * @private
         */
        private static processWrongInputs(wrongInputs: string[]) {

            wrongInputs.forEach((inputName: string) => {

                const element = DOM.Parser.getHtmlInputElementByName(inputName) as HTMLInputElement;
                element.classList.add("invalid");
            });
        }

        /**
         * Update snippet elements
         * @param snippets - list of snippets with HTML data
         * @param requestParams
         * @param xhr
         * @private
         */
        private static processPayloadSnippets(snippets: any, requestParams: INetworkingCreateRequestParameters, xhr: XMLHttpRequest) {

            const keys = Object.getOwnPropertyNames(snippets);
            keys.forEach((key: string) => {

                try {
                    const snippetElement = DOM.Parser.getHtmlElement(key);
                    snippetElement.innerHTML = snippets[key];
                    if (requestParams.snippetCallback) { requestParams.snippetCallback(key, snippetElement); }
                    this.processLinks(snippetElement);
                    this.processSubMenu(snippetElement);
                    if (!requestParams.dontInitTableTriggers) { this.processTables(snippetElement); }
                }  catch (e) {
                    SentryReportNetworkingParseException(xhr, e, requestParams)
                }
            });
        }

        /**
         * Process redirect payload
         * @param redirect - url to redirect
         * @param timeout - use timeout on redirect
         * @private
         */
        private static processPayloadRedirect(redirect: string, timeout: boolean) {

            if (!timeout) { window.location.replace(redirect); }
            else { setTimeout(() => { window.location.replace(redirect); }, 2500) }
        }

        /**
         *
         * @param pageTitle
         * @private
         */
        private static processPayloadTitle(pageTitle: PageTitle) {
            try { DOM.Parser.getHtmlElement('content-title').innerText = pageTitle.title; } catch (e) {}
        }

        /**
         * Process request to close modal
         * @private
         */
        private static processCloseModal() {

            const shownModal = Modal.Helpers.getShownModal();
            if (shownModal) { Modal.Helpers.closeModalByElement(shownModal); }
        }

        /**
         * Process custom javascript links in snippets
         * @param snippetElement
         * @private
         */
        private static processLinks(snippetElement: HTMLElement) {

            const triggers = DOM.Parser.getElementsByDataName('redirect-url', snippetElement, true);
            for (let i = 0; i < triggers.length; i++) {
                triggers[i].addEventListener('click', function() {
                    window.location.replace(DOM.Parser.getDataByNameFromElement('redirectUrl', triggers[i]))
                })
            }
        }

        /**
         * Process table triggers
         * @param snippetElement
         * @private
         */
        private static processTables(snippetElement: HTMLElement) {

            if (snippetElement.getElementsByClassName(ControlsGeneralTableControl.Config.tableControlElementClassName).length > 0) {
                ControlsGeneralTableControl.Control.trigger(null, snippetElement);
            }
        }


        /**
         * Process custom sub-menu mobile toggle in snippets
         * @param snippetElement
         * @private
         */
        private static processSubMenu(snippetElement: HTMLElement) {

            const toggle = snippetElement.getElementsByClassName('design-customPanel');
            if (toggle.length !== 1) { return; }
            const toggleParent = toggle[0].parentElement;
            if (!toggleParent) { return; }

            const trigger = toggle[0].getElementsByClassName('design-customPanel-control');
            if (trigger.length !== 1) { return; }

            trigger[0].addEventListener('click', () => {
                toggleParent.classList.toggle('menu-open');
            });
        }

        /**
         * Append request to the list
         * @private
         */
        private static appendRequest(requestIdentifier: string) {

            const location = window.location.href;
            let storageData : NetworkControllerStorageData = Storage.StorageController.getFromStorage('NetworkingController');
            if (storageData && location === storageData.location) {
                storageData.requestsList.push(requestIdentifier)
            } else {
                storageData = { requestsList: [requestIdentifier], location: location };
            } Storage.StorageController.saveToStorage('NetworkingController', storageData);
        }

        public static removeRequests() {

            const location = window.location.href;
            const storageData = { requestsList: [], location: location };
            Storage.StorageController.saveToStorage('NetworkingController', storageData);
        }

        /**
         * Remove request from the list
         * @private
         */
        private static removeRequest(requestIdentifier: string) {

            const location = window.location.href;
            let storageData : NetworkControllerStorageData = Storage.StorageController.getFromStorage('NetworkingController');
            if (storageData && location === storageData.location) {
                const index = storageData.requestsList.indexOf(requestIdentifier);
                if (index !== -1) { storageData.requestsList.splice(index, 1); }
            } else {
                storageData = { requestsList: [], location: location };
            }

            Storage.StorageController.saveToStorage('NetworkingController', storageData);
        }

        /**
         * Check if selected request already running
         * @private
         */
        private static alreadyRunningRequestByIdentifier(requestIdentifier: string): boolean {

            const location = window.location.href;
            let storageData : NetworkControllerStorageData = Storage.StorageController.getFromStorage('NetworkingController');
            if (storageData && location === storageData.location) {
                return storageData.requestsList.includes(requestIdentifier);
            } else {
                return false;
            }
        }

        /**
         * When leaving page finish all requests with abort
         * Also work with control panel change view
         * @param xhr - xhr of current request
         * @private
         */
        private static processAbortOnRedirect(xhr: XMLHttpRequest) : void {

            // Control panel elements
            const controlPanelElements = DOM.Parser.getHtmlElements('ControlPanelControl-inside-items-container-item', undefined, true);
            for (let i = 0; i < controlPanelElements.length; i++) {
                if (DOM.Parser.getDataByNameFromElementOrNull('disableAbort', controlPanelElements[i]) === 'yes') { continue; }
                controlPanelElements[i].addEventListener('click', () => { xhr.abort(); });
            }

            // Leaving page
            window.addEventListener('beforeunload', () => {
                xhr.abort();
            });
        }
    }
}